// Componente de pedidos con errores
// Orders with errors component

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { DonutChart } from './DonutChart';
import { useErrorsKpis } from '../../hooks/useErrorsKpis';

interface OrdersWithErrorsProps {
  filters?: {
    shop_id?: number;
    cutoff_time?: string;
    date?: string;
  };
}

export const OrdersWithErrors = ({ filters = {} }: OrdersWithErrorsProps) => {
  const { t } = useTranslation('homeModule');
  const { errorsKpis, isLoading } = useErrorsKpis(filters);

  // Mapear datos de la API al formato del gráfico
  const errorData = [
    {
      id: 'stock-errors',
      label: 'Sin Stock',
      value: errorsKpis.order_with_stock_errors,
      color: '#ef4444', // Rojo
      percentage:
        errorsKpis.total_orders_with_errors > 0
          ? (errorsKpis.order_with_stock_errors / errorsKpis.total_orders_with_errors) * 100
          : 0,
    },
    {
      id: 'unlinked-product',
      label: 'Producto Sin Vincular',
      value: errorsKpis.order_with_unlinked_product,
      color: '#8b5cf6', // Púrpura
      percentage:
        errorsKpis.total_orders_with_errors > 0
          ? (errorsKpis.order_with_unlinked_product / errorsKpis.total_orders_with_errors) * 100
          : 0,
    },
    {
      id: 'payment-errors',
      label: 'Sin Método de Pago',
      value: errorsKpis.order_with_payment_errors,
      color: '#3b82f6', // Azul
      percentage:
        errorsKpis.total_orders_with_errors > 0
          ? (errorsKpis.order_with_payment_errors / errorsKpis.total_orders_with_errors) * 100
          : 0,
    },
    {
      id: 'incorrect-address',
      label: 'Dirección Incorrecta',
      value: errorsKpis.order_with_incorrect_address,
      color: '#f97316', // Naranja
      percentage:
        errorsKpis.total_orders_with_errors > 0
          ? (errorsKpis.order_with_incorrect_address / errorsKpis.total_orders_with_errors) * 100
          : 0,
    },
    {
      id: 'other-errors',
      label: 'Otros Errores',
      value: errorsKpis.order_with_other_errors,
      color: '#6b7280', // Gris
      percentage:
        errorsKpis.total_orders_with_errors > 0
          ? (errorsKpis.order_with_other_errors / errorsKpis.total_orders_with_errors) * 100
          : 0,
    },
  ];

  const totalErrors = errorsKpis.total_orders_with_errors;

  if (isLoading) {
    return null;
  }

  return (
    <SectionBox className="bg-card-bg flex flex-col" data-testid="orders-with-errors">
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 mb-4 justify-between">
          <h3 className="text-sm font-medium">{t('errors.title')}</h3>
          <div className="w-8 h-8 rounded-md bg-card-border flex items-center justify-center">
            <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4 text-error" />
          </div>
        </div>

        {/* Gráfico donut interactivo */}
        <div className="flex justify-center items-center flex-1">
          <DonutChart data={errorData} total={totalErrors} size={170} />
        </div>
      </div>
    </SectionBox>
  );
};
